import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './shell/AppShell';
import { CentroLayout } from './shell/CentroLayout';
import { RequirePermission } from './shell/RequirePermission';
import { Placeholder } from './shell/Placeholder';
import { CosmicLoader } from '@marcianus/design/components';
import { ROUTES } from './routes';

// Code-splitting por módulo: cada feature carrega só quando necessária.
// ── Centro (consola do organizador) ──
const OverviewPage = lazy(() => import('@/modules/overview/OverviewPage'));
const EventsListPage = lazy(() => import('@/modules/events/EventsListPage'));
const CreateEventPage = lazy(() => import('@/modules/events/CreateEventPage'));
const EventKpiPage = lazy(() => import('@/modules/analytics/EventKpiPage'));
const NetworkingPage = lazy(() => import('@/modules/analytics/NetworkingPage'));
const StandPipelinePage = lazy(() => import('@/modules/stand/StandPipelinePage'));
const OrgOnboardingPage = lazy(() => import('@/modules/org/OrgOnboardingPage'));
const UserOnboardingPage = lazy(() => import('@/modules/onboarding/UserOnboardingPage'));
const HexisPage = lazy(() => import('@/modules/hexis/pages/HexisPage'));
const NotificationsPage = lazy(() => import('@/modules/hexis/pages/NotificationsPage'));
const SalesPage = lazy(() => import('@/modules/tickets/pages/SalesPage'));
const CheckinPage = lazy(() => import('@/modules/tickets/pages/CheckinPage'));
const PlanPage = lazy(() => import('@/modules/billing/PlanPage'));
const PlusPage = lazy(() => import('@/modules/billing/PlusPage'));
const MembersPage = lazy(() => import('@/modules/team/MembersPage'));
const BrandingPage = lazy(() => import('@/modules/team/BrandingPage'));
// ── Tap (networking — portado da raiz) ──
const TapPage = lazy(() => import('@/modules/tap/TapPage'));
const ProfilePage = lazy(() => import('@/modules/tap/ProfilePage'));
// ── Eventos (participante / descoberta) ──
const AttendeeExplore = lazy(() => import('@/modules/attendee/ExplorePage'));
const AttendeeEventDetail = lazy(() => import('@/modules/attendee/EventDetailPage'));
const AttendeeTickets = lazy(() => import('@/modules/attendee/MyTicketsPage'));
const AttendeeWallet = lazy(() => import('@/modules/attendee/WalletPage'));
// ── Descobrir (no Centro) ──
const ContentListPage = lazy(() => import('@/modules/content/ContentListPage'));
const ContentDetailPage = lazy(() => import('@/modules/content/ContentDetailPage'));
const MarketplacePage = lazy(() => import('@/modules/marketplace/MarketplacePage'));
const CategoryPage = lazy(() => import('@/modules/marketplace/CategoryPage'));
const ProductPage = lazy(() => import('@/modules/marketplace/ProductPage'));
const ProfileSettingsPage = lazy(() => import('@/modules/profile/pages/ProfileSettingsPage'));
const AccountHubPage = lazy(() => import('@/modules/profile/pages/AccountHubPage'));
const TelegramLinkPage = lazy(() => import('@/modules/profile/pages/TelegramLinkPage'));
const SettingsPage = lazy(() => import('@/modules/profile/pages/SettingsPage'));
const PainelPage = lazy(() => import('@/modules/dashboard/PainelPage'));

const s = (el: ReactNode) => <Suspense fallback={<CosmicLoader full={false} />}>{el}</Suspense>;

/**
 * Rota guardada por CAPACIDADE (não por papel).
 *
 * A string é exatamente a mesma que o backend exige na rota correspondente — é
 * essa simetria que impede o ecrã "fantasma" (abre, e depois enche de 403).
 * Quem não tem a capacidade nem chega a carregar o bundle da página.
 */
const p = (permission: string, el: ReactNode) => (
  <RequirePermission permission={permission}>{s(el)}</RequirePermission>
);

// basename derivado do base do Vite (ex.: '/lean/' → '/lean') — suporta deploy
// em sub-caminho da Hostinger sem partir os links (History API).
const BASENAME = import.meta.env.BASE_URL.replace(/\/+$/, '') || '/';

// ── App convergida: shell único de 3 tabs (Eventos · Tap · Centro) + Hexis. ──
export const router = createBrowserRouter([
  { path: ROUTES.org_onboarding, element: s(<OrgOnboardingPage />) },
  // Onboarding por passos — cada passo tem rota própria (deep-link + progresso).
  { path: '/welcome', element: s(<UserOnboardingPage />) },
  { path: '/welcome/perfil', element: s(<UserOnboardingPage />) },
  { path: '/welcome/objetivos', element: s(<UserOnboardingPage />) },
  { path: '/welcome/pronto', element: s(<UserOnboardingPage />) },
  {
    path: '/',
    element: <AppShell />,
    children: [
      { index: true, element: <Navigate to={ROUTES.attendee.explore} replace /> },

      // ── Tab 1 · EVENTOS ──
      { path: 'eventos', element: s(<AttendeeExplore />) },
      { path: 'eventos/:slug', element: s(<AttendeeEventDetail />) },
      { path: 'bilhetes', element: s(<AttendeeTickets />) },
      { path: 'carteira', element: s(<AttendeeWallet />) },
      { path: 'conteudos', element: s(<ContentListPage />) },
      { path: 'conteudos/:slug', element: s(<ContentDetailPage />) },
      { path: 'marketplace', element: s(<MarketplacePage />) },
      // A categoria vive em /c/:cat para não colidir com /marketplace/:slug — sem
      // isto, uma categoria chamada «saas» e um produto de slug «saas» disputariam
      // o mesmo URL, e ganharia o que estivesse primeiro no array.
      { path: 'marketplace/c/:cat', element: s(<CategoryPage />) },
      { path: 'marketplace/:slug', element: s(<ProductPage />) },
      // Marcianus+ do leitor: SEM guarda de permissão. A página do organizador
      // (billing/plano) exige `org.billing.view` — mandar um leitor para lá era
      // dar-lhe um 403 como resposta a «quero pagar-vos».
      { path: 'marcianus-plus', element: s(<PlusPage />) },
      { path: 'conta', element: s(<AccountHubPage />) },
      { path: 'conta/perfil', element: s(<ProfileSettingsPage />) },
      { path: 'conta/telegram', element: s(<TelegramLinkPage />) },
      { path: 'conta/definicoes', element: s(<SettingsPage />) },

      // ── Tab 2 · TAP (networking — portado da raiz) ──
      { path: 'tap', element: s(<TapPage />) },
      { path: 'tap/u/:id', element: s(<ProfilePage />) },
      { path: 'perfil/:id', element: s(<ProfilePage />) },
      { path: 'ligacoes', element: s(<TapPage />) },

      // ── Hexis — copiloto em tela cheia (FAB → rota /hexis) ──
      { path: 'hexis', element: s(<HexisPage />) },
      // ── Notificações — rota própria (sino → /notificacoes) ──
      { path: 'notificacoes', element: s(<NotificationsPage />) },

      // ── Tab 3 · DESCOBRIR (hub role-aware) — subtelas no CentroLayout ──
      { path: 'centro', element: <Navigate to={ROUTES.overview} replace /> },  // compat: /centro → /descobrir
      {
        path: 'descobrir',
        element: <CentroLayout />,
        children: [
          { index: true, element: s(<OverviewPage />) },
          { path: 'painel', element: s(<PainelPage />) },
          // A visão de ecossistema (equipa Marcianus) vive na consola HQ
          // (hq.marcianusexperience.com) — o www é só produto.
          { path: 'eventos', element: p('org.events.view', <EventsListPage />) },
          { path: 'eventos/novo', element: p('org.events.manage', <CreateEventPage />) },
          { path: 'eventos/:id/kpis', element: p('org.analytics.view', <EventKpiPage />) },
          { path: 'eventos/:id/networking', element: p('org.analytics.view', <NetworkingPage />) },
          { path: 'vendas', element: p('org.sales.view', <SalesPage />) },
          { path: 'checkin', element: p('org.checkin.operate', <CheckinPage />) },
          { path: 'stand', element: s(<StandPipelinePage />) },
          // O Estúdio (CMS) migrou para a consola HQ (/conteudos em
          // admin-sistemamarcianus) — publicar é trabalho da equipa, não do www.
          { path: 'equipa', element: <Navigate to={ROUTES.team.members} replace /> },
          { path: 'equipa/membros', element: p('org.team.view', <MembersPage />) },
          { path: 'equipa/marca', element: p('org.branding.manage', <BrandingPage />) },
          { path: 'billing', element: <Navigate to={ROUTES.billing.plan} replace /> },
          { path: 'billing/plano', element: p('org.billing.view', <PlanPage />) },
        ],
      },

      { path: '*', element: <Placeholder title="Página não encontrada" /> },
    ],
  },
], { basename: BASENAME });
